import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Film, MapPin, Calendar, FileVideo, AlertCircle, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

const CLASSIFICATIONS = [
    "All",
    "Military Observation",
    "Pilot Observation",
    "Radar Event",
    "UAP Video",
    "Government Release",
    "Historical Case",
    "Object Recovery",
    "Witness Encounter",
    "Unverified Media",
];

// Reliability tier — separate axis from content classification.
// Highest authority on top; "Under Investigation" / "Unverified Media" sit at the open end.
const RELIABILITY_TIERS = [
    "All",
    "Government Released",
    "Historical Archive",
    "Witness Submitted",
    "Archive Footage",
    "Under Investigation",
    "Unverified Media",
];

const RELIABILITY_STYLE = {
    "Government Released":   { color: "#9bb8d4", bg: "rgba(155,184,212,0.10)" },
    "Historical Archive":    { color: "#9bb8d4", bg: "rgba(155,184,212,0.10)" },
    "Witness Submitted":     { color: "#a8c79d", bg: "rgba(168,199,157,0.10)" },
    "Archive Footage":       { color: "#c4d8ea", bg: "rgba(196,216,234,0.10)" },
    "Under Investigation":   { color: "#e6c47a", bg: "rgba(230,196,122,0.10)" },
    "Unverified Media":      { color: "#e6c47a", bg: "rgba(230,196,122,0.12)" },
};

export default function MediaArchive() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [classification, setClassification] = useState("All");
    const [reliability, setReliability] = useState("All");

    useEffect(() => {
        let active = true;
        setLoading(true);
        const params = classification && classification !== "All" ? `?classification=${encodeURIComponent(classification)}` : "";
        api.get(`/media-archive${params}`).then((r) => {
            if (active) {
                setItems(r.data.items || []);
                setLoading(false);
            }
        }).catch(() => active && setLoading(false));
        return () => { active = false; };
    }, [classification]);

    const filtered = reliability === "All" ? items : items.filter((e) => (e.reliability || "") === reliability);

    return (
        <div className="ok-container py-10 lg:py-14" data-testid="media-archive-page">
            {/* Header */}
            <div className="mb-10">
                <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[var(--ok-accent)] mb-3 flex items-center gap-2">
                    <Film className="w-3 h-3" /> Section 09 · Media Archive
                </div>
                <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-3">Media Archive</h1>
                <p className="max-w-3xl text-sm sm:text-base opacity-80 leading-relaxed">
                    Declassified video, government press-releases, witness recordings, recovered-object exhibits and disputed material under active investigation. Each entry carries its own dossier: overview, source history, claims, arguments and embedded media. The archive documents the existence of the material; it does not assert its validity.
                </p>
            </div>

            {/* Reliability tier filter (primary) */}
            <div className="mb-5" data-testid="media-archive-reliability-filter">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-60 mb-2">Reliability tier</div>
                <div className="flex flex-wrap gap-2">
                    {RELIABILITY_TIERS.map((c) => {
                        const sel = reliability === c;
                        const style = RELIABILITY_STYLE[c];
                        return (
                            <button
                                key={c}
                                onClick={() => setReliability(c)}
                                className={`font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 transition-colors border ${sel ? "border-[var(--ok-accent)]" : "border-[var(--ok-border)] opacity-70 hover:opacity-100"}`}
                                style={sel ? { background: "var(--ok-accent)", color: "var(--ok-on-accent)", borderColor: "var(--ok-accent)" } : (style ? { color: style.color } : undefined)}
                                data-testid={`media-archive-reliability-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            >
                                {c}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Classification filter */}
            <div className="flex flex-wrap gap-2 mb-8" data-testid="media-archive-filter">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-60 w-full mb-1">Content classification</div>
                {CLASSIFICATIONS.map((c) => (
                    <button
                        key={c}
                        onClick={() => setClassification(c)}
                        className={`font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 transition-colors border ${classification === c ? "bg-[var(--ok-accent)] text-[color:var(--ok-on-accent)] border-[var(--ok-accent)]" : "border-[var(--ok-border)] opacity-70 hover:opacity-100"}`}
                        data-testid={`media-archive-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                        {c}
                    </button>
                ))}
            </div>

            {/* Grid */}
            {loading ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="ok-card aspect-[16/10] animate-pulse" style={{ background: "var(--ok-surface)" }} />
                    ))}
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filtered.map((e) => <MediaCard key={e.slug} e={e} />)}
                </div>
            )}

            {!loading && filtered.length === 0 && (
                <div className="ok-card p-10 text-center" data-testid="media-archive-empty">
                    <p className="opacity-70">No entries match the selected filters.</p>
                </div>
            )}
        </div>
    );
}

function MediaCard({ e }) {
    const isPending = e.pending_media_count > 0 && e.pending_media_count === e.media_count;
    const relStyle = e.reliability ? RELIABILITY_STYLE[e.reliability] : null;
    return (
        <Link
            to={`/media-archive/${e.slug}`}
            className="ok-card ok-card-hover overflow-hidden group flex flex-col"
            data-testid={`media-card-${e.slug}`}
        >
            <div className="aspect-[16/10] relative overflow-hidden flex items-center justify-center" style={{ background: "var(--ok-image-frame)" }}>
                {e.thumbnail_url ? (
                    <img src={e.thumbnail_url.startsWith("http") ? e.thumbnail_url : `${(import.meta.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "")}${e.thumbnail_url}`} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                ) : (
                    <Film className="w-12 h-12 opacity-30 text-[var(--ok-accent)]" />
                )}
                <div className="absolute inset-0 pointer-events-none" style={{ background: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n3'><feTurbulence type='fractalNoise' baseFrequency='1.1' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.55  0 0 0 0 0.65  0 0 0 0 0.78  0 0 0 0.28 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n3)'/></svg>\")", opacity: 0.45, mixBlendMode: "overlay" }} />
                {/* Reliability tier — top-right */}
                {e.reliability && (
                    <div className="absolute top-2 right-2 font-mono text-[9px] uppercase tracking-[0.25em] px-2 py-1" style={{ background: "var(--ok-chip-overlay-bg)", color: relStyle?.color || "#9bb8d4", border: `1px solid ${relStyle?.color ? relStyle.color : "rgba(155,184,212,0.4)"}` }}>
                        {e.reliability}
                    </div>
                )}
                {/* Classification chip — top-left */}
                <div className="absolute top-2 left-2 font-mono text-[9px] uppercase tracking-[0.25em] px-2 py-1" style={{ background: "var(--ok-chip-overlay-bg)", color: "var(--ok-chip-overlay-color)", border: "1px solid var(--ok-chip-overlay-border)" }}>
                    {e.classification}
                </div>
                {/* Media count / pending chip */}
                <div className="absolute bottom-2 right-2 font-mono text-[9px] uppercase tracking-[0.25em] px-2 py-1 flex items-center gap-1" style={{ background: "var(--ok-chip-overlay-bg)", color: isPending ? "#e6c47a" : "#9bb8d4" }}>
                    {isPending ? <AlertCircle className="w-3 h-3" /> : <FileVideo className="w-3 h-3" />}
                    {isPending ? "Pending upload" : `${e.media_count} media`}
                </div>
            </div>
            <div className="p-4 space-y-2 flex-1 flex flex-col">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-50 flex items-center gap-2">
                    <Calendar className="w-3 h-3" /> {e.date || e.year || "—"}
                    <span className="opacity-60">·</span>
                    <MapPin className="w-3 h-3" /> <span className="truncate">{e.location || "—"}</span>
                </div>
                <div className="font-display font-bold text-base leading-tight line-clamp-2 group-hover:text-[var(--ok-accent)] transition-colors">{e.title}</div>
                {e.summary && <p className="text-xs opacity-70 leading-relaxed line-clamp-3">{e.summary}</p>}
                <div className="flex-1" />
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ok-accent)] flex items-center gap-1 pt-2">
                    Open dossier <ArrowUpRight className="w-3 h-3" />
                </div>
            </div>
        </Link>
    );
}
